import httpServices from './httpServices'
import { jwtDecode } from "jwt-decode"

const TOKEN_KEY = "token";
const REFRESH_KEY = "refreshToken";

async function getUserData() {
    const response = await httpServices.get('/users/me');
    return response.data;
}

async function updateMe(data) {
    const response = await httpServices.put('/users/me', data);
    return response.data;
}

async function deleteMe() {
    const response = await httpServices.delete('/users/me');
    return response.data;
}

async function previewDeletion() {
    const response = await httpServices.get('/users/me/deletion-preview');
    return response.data;
}

async function requestAccountDeletion(email, confirm) {
    const response = await httpServices.post('/users/delete-account-request', { email, confirm });
    return response.data;
}

async function confirmAccountDeletion(token) {
    const response = await httpServices.post('/users/delete-account-confirm', { token });
    return response.data;
}

async function recordPlanInterest(plan, billingCycle) {
    const response = await httpServices.post('/users/me/plan-interest', { plan, billingCycle });
    return response.data;
}

async function claimInvitedBy(invitedByCode) {
    const response = await httpServices.post('/users/me/invited-by', { invitedByCode });
    return response.data;
}

async function setWritableGroups(groupIds) {
    const response = await httpServices.put('/users/me/writable-groups', { groupIds });
    return response.data;
}

async function login(user) {
    const response = await httpServices.post('/users/login', user)
    setTokens(response.data)
    return response.data.token
}

async function register(user) {
    const response = await httpServices.post('/users/register', user)
    setTokens(response.data)
    return response.data
}

async function forgotPassword(email) {
    const response = await httpServices.post('/users/forgot-password', { email })
    return response.data
}

async function resetPassword(token, password) {
    const response = await httpServices.post('/users/reset-password', { token, password })
    return response.data
}

async function verifyEmail(token) {
    const response = await httpServices.post('/users/verify-email', { token })
    return response.data
}

async function redeemAppleOffer(nonce) {
    const response = await httpServices.post('/users/me/apple-offer-redeemed', { nonce })
    return response.data
}

// invitedByCode: only meaningful on the sign-up branch of googleAuth (the
// backend ignores it entirely when the credential matches an existing
// account) — see controllers/userController.js.
async function googleLogin(credential, invitedByCode, signupConsent = {}) {
    const response = await httpServices.post('/users/google', {
        credential,
        invitedByCode,
        acceptedLegal: signupConsent.acceptedLegal,
    })
    setTokens(response.data)
    return response.data
}

async function googleLink(credential) {
    const response = await httpServices.post('/users/google/link', { credential })
    return response.data
}

async function appleLogin(credential, name, invitedByCode, signupConsent = {}) {
    const response = await httpServices.post('/users/apple', {
        credential,
        name,
        invitedByCode,
        acceptedLegal: signupConsent.acceptedLegal,
    })
    setTokens(response.data)
    return response.data
}

async function appleLink(credential) {
    const response = await httpServices.post('/users/apple/link', { credential })
    return response.data
}

function setTokens({ token, refreshToken } = {}) {
    if (token) {
        localStorage.setItem(TOKEN_KEY, token);
        // initNativePush() also runs at boot, but a fresh install is still
        // logged out then, so the token POST comes back 401 and is lost - the
        // plugin only hands a token back once per register(). Re-running it the
        // moment a session starts is what stops notifications from staying dead
        // until the next app launch. Imported lazily: nativePush imports this
        // module, and a static import would close the cycle.
        import('../utils/nativePush.js').then(m => m.initNativePush()).catch(() => {});
    } else {
        localStorage.removeItem(TOKEN_KEY);
    }
    if (refreshToken) {
        localStorage.setItem(REFRESH_KEY, refreshToken);
    } else if (!token) {
        localStorage.removeItem(REFRESH_KEY);
    }
}

// kept for the Google phone-completion flow which stores a bare token
function setToken(token) {
    setTokens({ token });
}

function logOut() {
    // best-effort server-side revocation of this device's refresh token
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (refreshToken) {
        httpServices.post('/users/logout', { refreshToken }).catch(() => {});
    }
    // the registered-token flag belongs to the account that just left
    import('../utils/nativePush.js').then(m => m.clearPushRegistered()).catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
}

function getJWT() {
    return localStorage.getItem(TOKEN_KEY);
}

function getUser() {
    try {
        const token = getJWT();
        const decoded = jwtDecode(token);
        // expired access token is fine while a refresh token exists — httpServices refreshes it
        if (decoded.exp && decoded.exp * 1000 < Date.now() && !localStorage.getItem(REFRESH_KEY)) {
            logOut();
            return null;
        }
        return decoded;
    } catch {
        return null;
    }
}

// --- web push ---

async function getPushPublicKey() {
    const response = await httpServices.get('/users/push-public-key');
    return response.data.publicKey;
}

async function subscribePush(subscription) {
    const response = await httpServices.post('/users/push-subscribe', { subscription });
    return response.data;
}

async function registerFcmToken(token, platform) {
    const response = await httpServices.post('/users/push-fcm-token', { token, platform });
    return response.data;
}

async function updatePushPreferences(prefs) {
    const response = await httpServices.patch('/users/me/push-preferences', prefs);
    return response.data;
}

export default {
    getUserData,
    updateMe,
    deleteMe,
    previewDeletion,
    requestAccountDeletion,
    confirmAccountDeletion,
    login,
    register,
    forgotPassword,
    resetPassword,
    verifyEmail,
    redeemAppleOffer,
    googleLogin,
    googleLink,
    appleLogin,
    appleLink,
    setToken,
    logOut,
    getJWT,
    getUser,
    getPushPublicKey,
    subscribePush,
    registerFcmToken,
    updatePushPreferences,
    recordPlanInterest,
    claimInvitedBy,
    setWritableGroups,
}
